import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '@/shared/api/base';
import { QUERY_KEYS } from '@/shared/config';
import type { Phoneboard } from '@/shared/lib/types';
import {
  getPhoneboardsOptions,
  searchPhoneboardsOptions,
  getPhoneboardStatusSummariesOptions,
  searchPhoneboardStatusSummariesOptions,
  searchGroupsOptions,
  updatePhoneboardMutation,
  registerPhoneboardMutation,
  deletePhoneboardMutation,
} from '@/shared/api/generated/@tanstack/react-query.gen';
import type {
  GetPhoneboardsData,
  SearchPhoneboardsData,
  GetPhoneboardStatusSummariesData,
  SearchPhoneboardStatusSummariesData,
  SearchGroupsData,
} from '@/shared/api/generated/types.gen';

export const phoneboardQueries = {
  all: (params?: GetPhoneboardsData['query']) =>
    getPhoneboardsOptions({ query: params }),

  search: (params?: SearchPhoneboardsData['query']) =>
    searchPhoneboardsOptions({ query: params }),

  detail: (id: string) => ({
    queryKey: [...QUERY_KEYS.phoneboards, id],
    queryFn: async (): Promise<Phoneboard> => {
      const { data } = await client.get({ url: `/phoneboards/${id}` });
      return data as Phoneboard;
    },
  }),

  statusSummaries: (params?: GetPhoneboardStatusSummariesData['query']) =>
    getPhoneboardStatusSummariesOptions({ query: params }),

  searchStatusSummaries: (params?: SearchPhoneboardStatusSummariesData['query']) =>
    searchPhoneboardStatusSummariesOptions({ query: params }),

  searchGroups: (params?: SearchGroupsData['query']) =>
    searchGroupsOptions({ query: params }),
};

export const usePhoneboards = (
  params?: GetPhoneboardsData['query'],
  enabled: boolean = true
) => {
  return useQuery({
    ...phoneboardQueries.all(params),
    enabled,
  });
};

export const usePhoneboardSearch = (
  params?: SearchPhoneboardsData['query'],
  enabled: boolean = true
) => {
  return useQuery({
    ...phoneboardQueries.search(params),
    enabled,
  });
};

export const usePhoneboard = (id: string) => {
  return useQuery(phoneboardQueries.detail(id));
};

export const usePhoneboardStatusSummaries = (
  params?: GetPhoneboardStatusSummariesData['query'],
  enabled: boolean = true
) => {
  return useQuery({
    ...phoneboardQueries.statusSummaries(params),
    enabled,
  });
};

export const usePhoneboardStatusSummarySearch = (
  params?: SearchPhoneboardStatusSummariesData['query'],
  enabled: boolean = true
) => {
  return useQuery({
    ...phoneboardQueries.searchStatusSummaries(params),
    enabled,
  });
};

export const useGroupsSearch = (
  params?: SearchGroupsData['query'],
  enabled: boolean = true
) => {
  return useQuery({
    ...phoneboardQueries.searchGroups(params),
    enabled,
  });
};

// Helper function to invalidate all phoneboard-related queries
const invalidatePhoneboardQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  // Invalidate legacy queries
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.phoneboards });
  
  // Invalidate OpenAPI generated queries
  queryClient.invalidateQueries({ 
    predicate: (query) => {
      const queryKey = query.queryKey;
      if (Array.isArray(queryKey) && queryKey.length > 0) {
        const key = queryKey[0];
        return typeof key === 'object' && key !== null && 
          '_id' in key && 
          (key._id === 'searchPhoneboards' || 
           key._id === 'getPhoneboards' || 
           key._id === 'searchPhoneboardStatusSummaries' ||
           key._id === 'getPhoneboardStatusSummaries' ||
           key._id === 'getPhoneboardById');
      }
      return false;
    }
  });
};

export const useCreatePhoneboard = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    ...registerPhoneboardMutation(),
    onSuccess: () => {
      invalidatePhoneboardQueries(queryClient);
    },
  });
};

export const useUpdatePhoneboard = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    ...updatePhoneboardMutation(),
    onSuccess: (_, variables) => {
      invalidatePhoneboardQueries(queryClient);
      if (variables.path?.id) {
        queryClient.invalidateQueries({ 
          queryKey: [...QUERY_KEYS.phoneboards, variables.path.id] 
        });
      }
    },
  });
};

export const useDeletePhoneboard = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    ...deletePhoneboardMutation(),
    onSuccess: (_, variables) => {
      invalidatePhoneboardQueries(queryClient);
      if (variables.path?.id) {
        queryClient.invalidateQueries({ 
          queryKey: [...QUERY_KEYS.phoneboards, variables.path.id] 
        });
      }
    },
  });
};